import { useLingui } from '@lingui/react/macro';
import { toast } from '@utima/ui';

import { trpc } from '@/services/trpc';

/**
 * Starts (or restarts) the admin OAuth flow for an MCP integration: opens the
 * provider consent popup and refreshes integration state when it closes. Shared
 * by the OAuth panel's Connect button and the Test/Sync "authorization required"
 * recovery path, so re-auth works identically from anywhere.
 */
export function useMcpOAuthConnect() {
  const { t } = useLingui();
  const utils = trpc.useUtils();
  const startOAuth = trpc.mcpIntegrations.startOAuth.useMutation();

  const refresh = () =>
    Promise.all([
      utils.mcpIntegrations.findAll.invalidate(),
      utils.mcpIntegrations.getOAuthStatus.invalidate(),
    ]);

  const connect = async (integrationId: string, returnPath?: string) => {
    try {
      const result = await startOAuth.mutateAsync({
        id: integrationId,
        returnPath: returnPath ?? `/agent-tools?tab=mcp&edit=${integrationId}`,
      });

      if (result.status === 'authorized') {
        toast.success(t`Connected.`);
        await refresh();

        return;
      }

      const popup = window.open(
        result.authorizationUrl,
        'mcp-oauth',
        'width=600,height=700,menubar=no,toolbar=no',
      );

      const timer = window.setInterval(() => {
        if (!popup || popup.closed) {
          window.clearInterval(timer);
          void refresh();
        }
      }, 1000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t`Connect failed.`);
    }
  };

  return { connect, isConnecting: startOAuth.isPending };
}
